import { lazy, Suspense, useState, useEffect, useCallback, type ReactNode } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { ThemeProvider } from './contexts/ThemeContext'
import { ConfirmationProvider } from './contexts/ConfirmationContext'
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext'
import { AuthProvider, useAuth, useUserRole } from '@hooks/useAuth'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { supabase } from '@services/supabase'
import { isPasswordRecoveryPending, detectRecoveryFromUrl } from '@utils/passwordRecovery'

// Debug utilities (development only)
if (import.meta.env.DEV) {
  import('./utils/testAuth').then(module => {
    window.testAuth = module.testAuth
  })
  import('./utils/clearImpersonation').then(module => {
    window.clearImpersonation = module.clearImpersonation
  })
}

import { isAcademyProductDomain } from '@utils/apiConfig'
import { appendMissionSearch } from '@config/features'

const StudentDashboardView = lazy(() => import('./components/views/StudentDashboardView'))
const TeacherDashboardView = lazy(() => import('./components/views/TeacherDashboardView'))
const TADashboardView = lazy(() => import('./components/views/TADashboardView'))
const SENCODashboardView = lazy(() => import('./components/views/SENCODashboardView'))
const SeniorStaffDashboardView = lazy(() => import('./components/views/SeniorStaffDashboardView'))
const SchoolAdminDashboardView = lazy(() =>
  import('./components/views/SchoolAdminDashboardView').then((module) => ({ default: module.SchoolAdminDashboardView }))
)
const DepartmentHeadDashboardView = lazy(() =>
  import('./components/views/DepartmentHeadDashboardView').then((module) => ({ default: module.DepartmentHeadDashboardView }))
)
const ParentDashboardView = lazy(() => import('./components/views/ParentDashboardView'))
const SysAdminDashboardView = lazy(() =>
  import('./components/views/SysAdminDashboardView').then((module) => ({ default: module.SysAdminDashboardView }))
)
const AcademyAdminDashboardView = lazy(() =>
  import('./components/views/AcademyAdminDashboardView').then((module) => ({ default: module.AcademyAdminDashboardView }))
)
const AcademyUserDashboardView = lazy(() =>
  import('./components/views/AcademyUserDashboardView').then((module) => ({ default: module.AcademyUserDashboardView }))
)
const LEAAdminDashboardView = lazy(() =>
  import('./components/views/LEAAdminDashboardView').then((module) => ({ default: module.LEAAdminDashboardView }))
)
const LEAUserDashboardView = lazy(() =>
  import('./components/views/LEAUserDashboardView').then((module) => ({ default: module.LEAUserDashboardView }))
)
const GovAdminDashboardView = lazy(() =>
  import('./components/views/GovAdminDashboardView').then((module) => ({ default: module.GovAdminDashboardView }))
)
const GovUserDashboardView = lazy(() =>
  import('./components/views/GovUserDashboardView').then((module) => ({ default: module.GovUserDashboardView }))
)
const DashboardView = lazy(() => import('./components/views/DashboardView'))
const PlannerView = lazy(() => import('./components/views/PlannerView'))
const ResourcesView = lazy(() => import('./components/views/ResourcesView'))
const StudentsView = lazy(() => import('./components/views/StudentsView'))
const AnalyticsView = lazy(() => import('./components/views/AnalyticsView'))
const LoginView = lazy(() => import('./components/views/LoginView'))
const MyLoginCallbackPage = lazy(() => import('./pages/MyLoginCallbackPage'))
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'))
const AcceptInvitation = lazy(() =>
  import('./pages/AcceptInvitation').then((module) => ({ default: module.AcceptInvitation }))
)
const AcceptMatInvitation = lazy(() =>
  import('./pages/AcceptMatInvitation').then((module) => ({ default: module.AcceptMatInvitation }))
)
const SignOrderFormPage = lazy(() => import('./pages/SignOrderFormPage'))
const ChangePassword = lazy(() =>
  import('./pages/ChangePassword').then((module) => ({ default: module.ChangePassword }))
)
const ParentInviteAcceptPage = lazy(() =>
  import('./pages/ParentInviteAcceptPage').then((module) => ({ default: module.ParentInviteAcceptPage }))
)
const SchoolManagement = lazy(() => import('./pages/SchoolManagement'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const SecurityCompliancePage = lazy(() => import('./pages/SecurityCompliancePage'))
const Landing = lazy(() => import('./pages/marketing/Landing'))
const Features = lazy(() => import('./pages/marketing/Features'))
const Pricing = lazy(() => import('./pages/marketing/Pricing'))
const BetaAccess = lazy(() => import('./pages/marketing/BetaAccess'))
const Investors = lazy(() => import('./pages/marketing/Investors'))
const ForgotPassword = lazy(() => import('./pages/marketing/ForgotPassword'))
const Register = lazy(() => import('./pages/public/Register'))
const MarketingLayout = lazy(() => import('./components/marketing/MarketingLayout'))
const ResourceViewer = lazy(() => import('./pages/ResourceViewer'))
const SchoolSettingsPage = lazy(() => import('./pages/SchoolSettingsPage'))
const MFASetupPage = lazy(() =>
  import('./pages/MFASetup').then((module) => ({ default: module.MFASetupPage }))
)
const SSOManagementPage = lazy(() =>
  import('./pages/SSOManagement').then((module) => ({ default: module.SSOManagementPage }))
)
const ReportBuilderPage = lazy(() => import('./pages/ReportBuilderPage'))
const NotificationPreview = lazy(() => import('./pages/NotificationPreview'))
const SupportTicketsListPage = lazy(() => import('./pages/SupportTicketsListPage'))
const SupportTicketCreatePage = lazy(() => import('./pages/SupportTicketCreatePage'))
const SupportTicketViewPage = lazy(() => import('./pages/SupportTicketViewPage'))
const SupportTicketNotificationConfigPage = lazy(() => import('./pages/SupportTicketNotificationConfigPage'))
const XmlWordEditorTestPage = lazy(() =>
  import('./pages/XmlWordEditorTestPage').then((module) => ({ default: module.XmlWordEditorTestPage }))
)
const OakContentExplorer = lazy(() =>
  import('./pages/OakContentExplorer').then((module) => ({ default: module.OakContentExplorer }))
)
const BillingReturnPage = lazy(() =>
  import('./pages/BillingReturnPage').then((module) => ({ default: module.BillingReturnPage }))
)

declare global {
  interface Window {
    testAuth?: unknown
    clearImpersonation?: unknown
    queryClient?: QueryClient
  }
}

// Create React Query client with optimized defaults to reduce API calls
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes - data is fresh for 10 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime) - keep in cache for 30 minutes
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: false, // Don't refetch on component mount if data is fresh
      refetchOnReconnect: true, // Only refetch on reconnect (network restored)
      retry: 1, // Only retry once on failure
      retryDelay: 1000 // Wait 1 second before retry
    }
  }
})

const DashboardRouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
    </div>
  </div>
)

const lazyDashboard = (children: ReactNode) => (
  <Suspense fallback={<DashboardRouteFallback />}>{children}</Suspense>
)

// Expose queryClient globally for logout cleanup (development/debugging)
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.queryClient = queryClient
}

// Role-based Dashboard Redirector
function DashboardRedirect() {
  const { user } = useAuth()
  const { role, loading: roleLoading } = useUserRole(user?.id)
  const location = useLocation()
  const [targetDashboard, setTargetDashboard] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [mustChangePassword, setMustChangePassword] = useState(false)

  const readLocalDashboard = useCallback(
    (userId?: string | null) => {
      if (!userId) return null
      try {
        return localStorage.getItem(`omnis_active_dashboard_${userId}`)
      } catch (storageError) {
        console.warn('🔀 DashboardRedirect - Failed to read local storage:', storageError)
        return null
      }
    },
    []
  )

  const resolveDashboardTarget = useCallback(
    (params: {
      active?: string | null
      default?: string | null
      allowed?: string[] | null
      local?: string | null
      fallbackRole?: string | null
    }) => {
      const { active, default: defaultDashboard, allowed, local, fallbackRole } = params
      const normalizedAllowed = Array.isArray(allowed) ? allowed.filter(Boolean) : []

      // CRITICAL: For sysadmin, always prioritize sysadmin dashboard
      // This prevents sysadmin from being redirected to other dashboards (e.g., student)
      if (fallbackRole === 'sysadmin') {
        // If sysadmin is in allowed dashboards, use it
        if (normalizedAllowed.includes('sysadmin')) {
          return 'sysadmin'
        }
        // Otherwise, still default to sysadmin for sysadmin role
        return 'sysadmin'
      }

      // For other roles, prioritize in this order:
      // 1. active_dashboard (if it's allowed)
      // 2. default_dashboard (if it's allowed)
      // 3. fallbackRole (user's actual role - if it's allowed)
      // 4. first allowed dashboard
      // 5. local preference (only if it's allowed)
      // 6. fallback to teacher

      const candidates = [
        active && normalizedAllowed.includes(active) ? active : null,
        defaultDashboard && normalizedAllowed.includes(defaultDashboard) ? defaultDashboard : null,
        fallbackRole && normalizedAllowed.includes(fallbackRole) ? fallbackRole : null,
        normalizedAllowed[0] || null,
        local && normalizedAllowed.includes(local) ? local : null,
        fallbackRole || 'teacher',
      ]

      const picked = candidates.find((value) => typeof value === 'string' && value.length > 0)
      if (!picked) return 'teacher'

      // Verify the picked dashboard is allowed
      const isAllowed =
        picked === active ||
        picked === defaultDashboard ||
        normalizedAllowed.includes(picked) ||
        picked === fallbackRole

      if (!isAllowed && normalizedAllowed.length > 0) {
        return normalizedAllowed[0]
      }

      return picked
    },
    []
  )

  useEffect(() => {
    const fetchTargetDashboard = async () => {
      if (!user?.id) {
        setLoading(false)
        return
      }

      const localPreference = readLocalDashboard(user.id)

      try {
        const { data, error } = await supabase
          .from('users')
          .select('active_dashboard, default_dashboard, allowed_dashboards, email, role, must_change_password')
          .eq('id', user.id)
          .single()

        if (error) throw error
        
        // Check if user must change password
        if (data?.must_change_password) {
          setMustChangePassword(true)
          setLoading(false)
          return
        }

        // Clear localStorage if it doesn't match the user's current role or allowed dashboards
        const allowedDashboards = Array.isArray(data?.allowed_dashboards) 
          ? data.allowed_dashboards.filter(Boolean) 
          : []
        const userRole = data?.role || role
        
        // CRITICAL: For sysadmin, always clear localStorage preferences that aren't sysadmin
        // This prevents sysadmin from being redirected to other dashboards
        let safeLocalPreference = localPreference
        if (userRole === 'sysadmin') {
          if (localPreference && localPreference !== 'sysadmin') {
            console.log(`🔀 DashboardRedirect - Clearing non-sysadmin localStorage preference for sysadmin user: ${localPreference}`)
            try {
              localStorage.removeItem(`omnis_active_dashboard_${user.id}`)
              safeLocalPreference = null // Clear the preference so it's not used
            } catch (e) {
              console.warn('Failed to clear localStorage:', e)
            }
          }
        } else if (localPreference && 
            localPreference !== data?.active_dashboard && 
            localPreference !== data?.default_dashboard &&
            !allowedDashboards.includes(localPreference) &&
            localPreference !== userRole) {
          console.log(`🔀 DashboardRedirect - Clearing invalid localStorage preference: ${localPreference}`)
          try {
            localStorage.removeItem(`omnis_active_dashboard_${user.id}`)
            safeLocalPreference = null
          } catch (e) {
            console.warn('Failed to clear localStorage:', e)
          }
        }

        const target = resolveDashboardTarget({
          active: data?.active_dashboard,
          default: data?.default_dashboard,
          allowed: allowedDashboards,
          local: safeLocalPreference,
          fallbackRole: data?.role || role,
        })

        console.log('🔀 DashboardRedirect - Resolved target:', {
          userId: user.id,
          target,
          active: data?.active_dashboard,
          default: data?.default_dashboard,
          allowed: allowedDashboards,
          role: data?.role,
          localPreference,
        })

        setTargetDashboard(target)
      } catch (error) {
        console.error('Failed to fetch target dashboard:', error)
        const fallback = resolveDashboardTarget({
          active: undefined,
          default: undefined,
          allowed: [],
          local: localPreference,
          fallbackRole: role,
        })
        setTargetDashboard(fallback)
      } finally {
        setLoading(false)
      }
    }

    fetchTargetDashboard()
  }, [user?.id, role, readLocalDashboard, resolveDashboardTarget])

  // Redirect to set-new-password (recovery email or temporary password)
  if (mustChangePassword || isPasswordRecoveryPending()) {
    return <Navigate to="/change-password" replace />
  }

  if (loading || roleLoading || !targetDashboard) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  // Redirect based on target dashboard (active, default, or first allowed)
  const dashboardRoutes: Record<string, string> = {
    sysadmin: '/sysadmin',
    school_admin: '/school-admin',
    senior_staff: '/senior-staff',
    department_head: '/department-head',
    teacher: '/teacher',
    ta: '/ta',
    senco: '/senco',
    student: '/student',
    parent: '/parent',
    academy_admin: '/academy-admin',
    academy_user: '/academy-user',
    lea_admin: '/lea-admin',
    lea_user: '/lea-user',
    gov_admin: '/gov-admin',
    gov_user: '/gov-user'
  }

  const redirectTo = dashboardRoutes[targetDashboard] || '/teacher'
  const redirectWithSearch =
    location.search && location.search.includes('mission=')
      ? `${redirectTo}${location.search}`
      : appendMissionSearch(redirectTo)
  
  console.log(`🔀 DashboardRedirect: User ${user?.email ?? 'unknown'} → ${targetDashboard} (${redirectWithSearch})`)
  
  return <Navigate to={redirectWithSearch} replace />
}

function AppContent() {
  const { user, loading } = useAuth()
  const academyProductDomain = isAcademyProductDomain()
  const [recoveryWaitExpired, setRecoveryWaitExpired] = useState(false)

  useEffect(() => {
    document.title = 'Qwixl:Academy'
  }, [])

  useEffect(() => {
    detectRecoveryFromUrl()
  }, [])

  const waitingForRecoverySession =
    !user && !recoveryWaitExpired && isPasswordRecoveryPending()

  useEffect(() => {
    if (!waitingForRecoverySession) return
    const t = window.setTimeout(() => setRecoveryWaitExpired(true), 12000)
    return () => window.clearTimeout(t)
  }, [waitingForRecoverySession])

  // Show loading while checking auth, or while recovery link session is established
  if (loading || waitingForRecoverySession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            {waitingForRecoverySession ? 'Opening password reset…' : 'Loading...'}
          </p>
        </div>
      </div>
    )
  }

  // Show marketing site and login when not authenticated
  if (!user) {
    return (
      <Router>
        <Suspense fallback={<DashboardRouteFallback />}>
          <Routes>
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<LoginView />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/accept-mat-invitation" element={<AcceptMatInvitation />} />
            <Route path="/auth/mylogin/callback" element={<MyLoginCallbackPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/invite/parent" element={<ParentInviteAcceptPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/security-compliance" element={<SecurityCompliancePage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {academyProductDomain ? (
              <>
                <Route path="/teacher" element={<Navigate to="/login" replace />} />
                <Route path="/student" element={<Navigate to="/login" replace />} />
                <Route path="/senco" element={<Navigate to="/login" replace />} />
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="/features" element={<Navigate to="/login" replace />} />
                <Route path="/pricing" element={<Navigate to="/login" replace />} />
                <Route path="/beta" element={<Navigate to="/login" replace />} />
                <Route path="/investors" element={<Navigate to="/login" replace />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </>
            ) : (
              <>
                <Route element={<MarketingLayout />}>
                  <Route path="/" element={<Landing />} />
                  <Route path="/features" element={<Features />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/beta" element={<BetaAccess />} />
                  <Route path="/investors" element={<Investors />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
          </Routes>
        </Suspense>
      </Router>
    )
  }

  // Authenticated - show role-based dashboard routes with SECURITY protection
  return (
    <Router>
      <Suspense fallback={<DashboardRouteFallback />}>
        <Routes>
        <Route path="/" element={<DashboardRedirect />} />
        <Route path="/dashboard" element={<DashboardRedirect />} />
        <Route path="/force-password" element={<Navigate to="/change-password" replace />} />
        <Route path="/invite/parent" element={<ParentInviteAcceptPage />} />
        {/* Keep OAuth callback mounted after exchangeCodeForSession sets a transient session */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/auth/mylogin/callback" element={<MyLoginCallbackPage />} />
        
        {/* PROTECTED ROUTES - Verify user role matches required role */}
        <Route path="/sysadmin" element={
          <ProtectedRoute requiredRole="sysadmin">
            <SysAdminDashboardView />
          </ProtectedRoute>
        } />
        <Route path="/school-admin" element={
          <ProtectedRoute requiredRole="school_admin">
            <SchoolAdminDashboardView />
          </ProtectedRoute>
        } />
        <Route path="/senior-staff" element={
          <ProtectedRoute requiredRole="senior_staff">
            <SeniorStaffDashboardView />
          </ProtectedRoute>
        } />
        <Route path="/department-head" element={
          <ProtectedRoute requiredRole="department_head">
            <DepartmentHeadDashboardView />
          </ProtectedRoute>
        } />
        <Route path="/teacher" element={
          <ProtectedRoute requiredRole="teacher">
            {lazyDashboard(<TeacherDashboardView />)}
          </ProtectedRoute>
        } />
        <Route path="/ta" element={
          <ProtectedRoute requiredRole="ta">
            {lazyDashboard(<TADashboardView />)}
          </ProtectedRoute>
        } />
        <Route path="/senco" element={
          <ProtectedRoute requiredRole="senco">
            {lazyDashboard(<SENCODashboardView />)}
          </ProtectedRoute>
        } />
        <Route path="/student" element={
          <ProtectedRoute requiredRole="student">
            {lazyDashboard(<StudentDashboardView />)}
          </ProtectedRoute>
        } />
        <Route path="/parent" element={
          <ProtectedRoute requiredRole="parent">
            <ParentDashboardView />
          </ProtectedRoute>
        } />
        <Route path="/academy-admin" element={
          <ProtectedRoute requiredRole="academy_admin">
            <AcademyAdminDashboardView />
          </ProtectedRoute>
        } />
        <Route path="/academy-user" element={
          <ProtectedRoute requiredRole="academy_user">
            <AcademyUserDashboardView />
          </ProtectedRoute>
        } />
        <Route path="/lea-admin" element={
          <ProtectedRoute requiredRole="lea_admin">
            <LEAAdminDashboardView />
          </ProtectedRoute>
        } />
        <Route path="/lea-user" element={
          <ProtectedRoute requiredRole="lea_user">
            <LEAUserDashboardView />
          </ProtectedRoute>
        } />
        <Route path="/gov-admin" element={
          <ProtectedRoute requiredRole="gov_admin">
            <GovAdminDashboardView />
          </ProtectedRoute>
        } />
        <Route path="/gov-user" element={
          <ProtectedRoute requiredRole="gov_user">
            <GovUserDashboardView />
          </ProtectedRoute>
        } />
        
        {/* Public Routes */}
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/accept-mat-invitation" element={<AcceptMatInvitation />} />
        <Route path="/sign-order-form/:orderFormId" element={<SignOrderFormPage />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/notification-preview" element={<NotificationPreview />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/security-compliance" element={<SecurityCompliancePage />} />
        
        {/* Admin Routes */}
        <Route path="/school-management" element={<SchoolManagement />} />
        <Route path="/resource-viewer" element={<ResourceViewer />} />
        <Route path="/reports" element={<ReportBuilderPage />} />
        <Route path="/user-management" element={<div className="p-6">User Management (Coming Soon)</div>} />
        <Route path="/billing-management" element={<div className="p-6">Billing Management (Coming Soon)</div>} />
        <Route path="/settings" element={<SchoolSettingsPage />} />
        <Route path="/billing-return" element={<BillingReturnPage />} />
        <Route path="/mfa-setup" element={<MFASetupPage />} />
        <Route path="/sso-management" element={<SSOManagementPage />} />
        
        {/* Support Tickets Routes */}
        <Route path="/support/tickets" element={<SupportTicketsListPage />} />
        <Route path="/support/tickets/create" element={<SupportTicketCreatePage />} />
        <Route path="/support/tickets/:ticketId" element={<SupportTicketViewPage />} />
        <Route path="/support/tickets/notifications" element={<SupportTicketNotificationConfigPage />} />
        
        {/* XML Word Editor Test Page (Development/Testing) */}
        <Route path="/xml-editor-test" element={<XmlWordEditorTestPage />} />
        <Route path="/oak-explorer" element={<OakContentExplorer />} />
        
        {/* Legacy routes (TODO: add protection if needed) */}
        <Route path="/dashboard-old" element={<DashboardView />} />
        <Route path="/planner" element={<PlannerView />} />
        <Route path="/resources" element={<ResourcesView />} />
        <Route path="/students" element={<StudentsView />} />
        <Route path="/analytics" element={<AnalyticsView />} />
        
        {/* Catch-all redirects */}
        <Route path="/login" element={<DashboardRedirect />} />
        <Route path="*" element={<DashboardRedirect />} />
        </Routes>
      </Suspense>
    </Router>
  )
}

// Main App with Providers
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ConfirmationProvider>
          <AuthProvider>
            <FeatureFlagsProvider>
              <AppContent />
            </FeatureFlagsProvider>
          </AuthProvider>
          <Toaster
          position="top-right"
          containerStyle={{ zIndex: 20000 }}
          toastOptions={{
            duration: 4000,
            style: {
              borderRadius: '8px',
              padding: '12px 16px',
              fontSize: '14px',
            },
          }}
        />
        </ConfirmationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
